import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { call, getJson, mediaUrl } from "@/lib/api";
import { postJson, useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import { AuthorLink, Avatar, JoinPrompt, PendingNote, useIsMine, useTimeAgo } from "@/components/community/shared";

export const Byline = ({ doc, onDelete, testid }) => {
  const { t } = useLang();
  const ago = useTimeAgo();
  const mine = useIsMine()(doc);
  return (
    <div className="flex items-center gap-3">
      <AuthorLink author={doc.author}><Avatar author={doc.author} size={34} /></AuthorLink>
      <div className="min-w-0 flex-1">
        <AuthorLink author={doc.author} className="block truncate text-sm font-semibold hover:text-[var(--green)]" testid={`author-${doc.id}`}>{doc.author?.name}</AuthorLink>
        <p className="mono text-[11px] text-[var(--muted)]">{ago(doc.created_at)}</p>
      </div>
      {mine && <button type="button" onClick={onDelete} className="icon-btn" aria-label={t("com.delete")} data-testid={testid}><Trash2 size={14} /></button>}
    </div>
  );
};

/** Reply/comment composer. endpoint = POST path, next = where to come back after signing in. */
export const ReplyForm = ({ endpoint, next, onReplied, testid = "reply" }) => {
  const { t } = useLang();
  const { member } = useAuth();
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(false);
  if (!member) return <JoinPrompt next={next} />;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await postJson(endpoint, { body });
      setBody("");
      setPending(r.status === "pending");
      if (r.status === "published") onReplied(r);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="rounded-2xl border border-white/10 bg-[var(--card)] p-4" data-testid={`${testid}-form`}>
      <textarea value={body} onChange={(e) => setBody(e.target.value)} required maxLength={2000} rows={3} placeholder={t("com.replyPh")} className="support-input resize-none" data-testid={`${testid}-input`} />
      <div className="mt-3 flex justify-end">
        <button type="submit" disabled={busy || !body.trim()} className="btn-primary !py-2 disabled:opacity-50" data-testid={`${testid}-submit-btn`}>
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} {t("com.reply")}
        </button>
      </div>
      <PendingNote show={pending} />
    </form>
  );
};

export default function Topic() {
  const { id } = useParams();
  const { t } = useLang();
  const navigate = useNavigate();
  const [data, setData] = useState(undefined);
  useEffect(() => {
    getJson(`/community/topics/${id}`).then(setData);
  }, [id]);
  const del = async (kind, docId) => {
    if (!window.confirm(t("com.confirmDelete"))) return;
    await call(`/community/${kind}/${docId}`, { method: "DELETE" });
    if (kind === "topic") navigate("/comunidad");
    else setData((d) => ({ ...d, replies: d.replies.filter((r) => r.id !== docId) }));
  };
  if (data === undefined) return <main className="grid min-h-screen place-items-center"><Loader2 className="animate-spin" /></main>;
  return (
    <main className="px-5 pb-28 pt-32 md:px-10" data-testid="topic-page">
      <div className="mx-auto max-w-3xl">
        <Link to="/comunidad" className="mono inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[var(--muted)] hover:text-[var(--ink)]" data-testid="topic-back-link"><ArrowLeft size={14} /> {t("com.backForums")}</Link>
        {!data ? (
          <p className="font-display mt-10 text-3xl" data-testid="topic-not-found">{t("com.notFound")}</p>
        ) : (
          <>
            <article className="mt-8" data-testid="topic-main">
              <p className="mono text-[11px] uppercase tracking-[0.16em] text-[var(--green)]">{t(`fcat.${data.topic.category}`)}</p>
              <h1 className="font-display mt-2 text-4xl leading-tight sm:text-5xl" data-testid="topic-title">{data.topic.title}</h1>
              <div className="mt-6"><Byline doc={data.topic} onDelete={() => del("topic", data.topic.id)} testid="topic-delete-btn" /></div>
              <p className="mt-6 whitespace-pre-line leading-relaxed" data-testid="topic-body">{data.topic.body}</p>
              {data.topic.image_url && <img src={mediaUrl(data.topic.image_url)} alt="" className="mt-6 w-full rounded-2xl" />}
            </article>
            <h2 className="font-display mt-14 text-2xl" data-testid="topic-replies-count">{t("com.replies", { n: data.replies.length })}</h2>
            <div className="mt-4 space-y-4">
              {data.replies.map((r) => (
                <div key={r.id} className="rounded-2xl border border-white/10 bg-[var(--card)] p-4" data-testid={`reply-${r.id}`}>
                  <Byline doc={r} onDelete={() => del("reply", r.id)} testid={`reply-delete-${r.id}`} />
                  <p className="mt-3 whitespace-pre-line text-sm leading-relaxed">{r.body}</p>
                </div>
              ))}
            </div>
            <div className="mt-8"><ReplyForm endpoint={`/community/topics/${data.topic.id}/replies`} next={`/comunidad/tema/${data.topic.id}`} onReplied={(r) => setData((d) => ({ ...d, replies: [...d.replies, r] }))} /></div>
          </>
        )}
      </div>
    </main>
  );
}
